import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/locations/select';
export const getCrmBrandLocationsSelectEndpointIdentifier = endpoint;

export interface ICrmBrandLocationsData {
  id: number;
  name: string;
  address: string;
  area: string;
  geoLocation: {
    id: number;
    name: string;
  };
}

type TData = {
  pageCount: number;
  total: number;
  data: ICrmBrandLocationsData[];
};

type TError = AxiosError;
interface AdditionalParams {
  brandId?: number;
  page?: number;
  limit?: number;
}

type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    brandId: TParam;
    name: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmBrandLocationsSelectApi(additionalParams?: AdditionalParams, options?: Options): ReturnType {
  const toast = useCustomToast();
  const defaultPage = getValidatedPageParam();
  const defaultLimit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name } = getPageParams();
  const { brandId: additionalBrandId, page: additionalPage, limit: additionalLimit } = additionalParams || {};
  const { ...otherOptions } = options || {};

  // Use additional params if provided, otherwise use defaults
  const page = additionalPage || defaultPage;
  const limit = additionalLimit || defaultLimit;
  const brandId = additionalBrandId || null;

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
      };

      if (brandId) {
        params.brandId = brandId;
      }

      if (name) {
        params.name = name;
      }

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, brandId, name }], queryFn, {
    enabled: isLoggedIn && !!brandId,
    ...otherOptions,
  });
}
